/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef } from 'react'
import Slider, { Settings as SliderSettings } from 'react-slick'
import styled from 'styled-components'
import GameAttendanceCard from '../GameAttendanceCard'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { UpcomingGame } from '@sportninja/common/types/PlayerAttendance'
import {
  checkTeamAssignment,
  formatShortGameDate,
} from '@sportninja/common/ts-utils'
import {
  getDayjsTZDate,
  getDayjsTZDatWithoutTime,
} from '@sportninja/common/utils/utils'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { trackEvent, getPlatformType } from '../../utils/analytics'

const PrevArrow = (props: any) => {
  const { className, onClick, currentSlide } = props
  return (
    <div
      className={`${className} ${currentSlide === 0 ? 'slick-disabled' : ''}`}
      onClick={onClick}
      style={{ display: currentSlide === 0 ? 'none' : 'block' }}
    />
  )
}

const NextArrow = (props: any) => {
  const { className, onClick, slideCount, currentSlide } = props
  return (
    <div
      className={`${className} ${
        currentSlide === slideCount - 1 ? 'slick-disabled' : ''
      }`}
      onClick={onClick}
      style={{ display: currentSlide === slideCount - 1 ? 'none' : 'block' }}
    />
  )
}

interface GameAttendanceCarouselProps {
  attendance: UpcomingGame[]
  readAttendanceInformation: () => void
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  onPressManageRefreshments: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  onPressManageVideographer?: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
}

const GameAttendanceCarousel: React.FC<GameAttendanceCarouselProps> = ({
  attendance,
  readAttendanceInformation,
  onPressManageAttendance,
  onPressManageRefreshments,
  onPressManageVideographer,
}) => {
  const gamesCardsLength = attendance.length
  const isEmpty = useMemo(() => {
    return attendance?.length === 0
  }, [attendance])

  const hasFiredViewRef = useRef(false)
  useEffect(() => {
    if (attendance?.length > 0 && !hasFiredViewRef.current) {
      hasFiredViewRef.current = true
      const hasPlayerAttendance = attendance.some(
        (g) => g?.myPlayers && g.myPlayers.length > 0
      )
      const hasTeamRepAttendance = attendance.some(
        (g) => g?.myTeamOfficials && g.myTeamOfficials.length > 0
      )
      const hasVideoEnabled = attendance.some((g) => g?.show_video)
      trackEvent('home_game_card_viewed', {
        platform: getPlatformType(),
        total_games: attendance.length,
        has_player_attendance: hasPlayerAttendance,
        has_team_rep_attendance: hasTeamRepAttendance,
        has_video_enabled: hasVideoEnabled,
      })
    }
  }, [attendance])

  if (isEmpty) {
    return null
  }

  const settings: SliderSettings = {
    dots: gamesCardsLength > 1 ? true : false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    customPaging: (_i: number) => <CustomDot />,
    className: 'slick-carousel',
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    adaptiveHeight: true,
    afterChange: (currentSlide: number) => {
      if (currentSlide > 0 && attendance?.[currentSlide]) {
        trackEvent('home_game_card_slide_changed', {
          platform: getPlatformType(),
          game_id: attendance[currentSlide]?.id,
          slide_index: currentSlide,
          total_games: attendance.length,
        })
      }
    },
  }

  const statusToString = (s: number | null): string => {
    if (s === null || s === undefined) {
      return 'cleared'
    }
    switch (s) {
      case 0:
        return 'attending'
      case 1:
        return 'not_attending'
      case 2:
        return 'maybe'
      default:
        return 'unknown'
    }
  }

  const updateAttendanceStatus = async ({
    playerId,
    gameId,
    rosterId,
    status,
  }: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => {
    const game = attendance?.find((g) => g.id === gameId)
    const player = game?.myPlayers?.find(
      (p) => p.id === playerId && p.game_player_roster_id === rosterId
    )
    const previousStatus = player?.attendance_status
    const isToggleOff = previousStatus === status
    const resolvedStatus = isToggleOff ? null : status
    const teamSide =
      player?.team_id === game?.home_team?.id ? 'home' : 'visitor'

    trackEvent('home_game_card_attendance_selected', {
      platform: getPlatformType(),
      game_id: gameId,
      attendance_status: isToggleOff ? 'cleared' : statusToString(status),
      previous_status: statusToString(previousStatus ?? null),
      team_side: teamSide,
      is_toggle_off: isToggleOff,
    })

    try {
      await req(
        `/games/${gameId}/rosters/${rosterId}/players/${playerId}/attendance`,
        {
          method: 'PUT',
          body: JSON.stringify({
            attendance_status: resolvedStatus,
          }),
        }
      )
      await readAttendanceInformation()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <CarouselContainer>
      <StyledSlider {...settings}>
        {attendance?.map((game: UpcomingGame, index) => {
          const { isPlayingOnHomeTeam, isPlayingOnAwayTeam } =
            checkTeamAssignment(game)
          const gameDate = getDayjsTZDate(
            game?.starts_at,
            game?.location?.timezone
          )
          const isGameTimeToBeDefined = isTimeToBeDefined(game?.starts_at)
          const gameDateWithoutTime = getDayjsTZDatWithoutTime(
            game?.starts_at,
            game?.location?.timezone
          )
          const playersAttendance = game?.myPlayers
          const teamRepsAttendance = game?.myTeamOfficials
          const dateTime = formatShortGameDate(gameDate, true)
          const homeTeam = {
            name: game?.home_team?.name,
            logo: resolveAssetUrl(game?.home_team?.image) ?? '',
          }
          const awayTeam = {
            name: game?.visiting_team?.name,
            logo: resolveAssetUrl(game?.visiting_team?.image) ?? '',
          }
          const isFirstGame = index === 0

          return (
            <div key={index}>
              <GameAttendanceCard
                onPressManageAttendance={onPressManageAttendance}
                isFirstGame={isFirstGame}
                rowItem={game}
                playersAttendance={playersAttendance}
                teamRepsAttendance={teamRepsAttendance}
                homeTeam={homeTeam}
                awayTeam={awayTeam}
                gameTime={dateTime}
                leagueName={game.schedule}
                arenaName={game.facility}
                facilityNumber={game.location.name}
                isPlayingOnHomeTeam={isPlayingOnHomeTeam}
                isPlayingOnAwayTeam={isPlayingOnAwayTeam}
                onAttendanceSelect={updateAttendanceStatus}
                onPressManageRefreshments={onPressManageRefreshments}
                onPressManageVideographer={onPressManageVideographer}
                isGameTimeToBeDefined={isGameTimeToBeDefined}
                gameDateWithoutTime={gameDateWithoutTime}
              />
            </div>
          )
        })}
      </StyledSlider>
    </CarouselContainer>
  )
}

const CarouselContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  margin-bottom: 32px;
`

const StyledSlider = styled(Slider)`
  /* this is the default style of the dots */
  /* .slick-dots {
    position: absolute;
    bottom: -25px;
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: center;
} */

  .slick-dots {
    /* center the dots */
    display: flex !important;
    justify-content: center;
    align-items: center;
    bottom: -24px;
    gap: 8px;
    li {
      margin: 0 0;
      width: 8px !important;
      height: 8px !important;
    }
  }
  /* remove the margin of the arrows */
  .slick-arrow {
    margin: 0 16px;
    z-index: 1;
  }

  /* the slides */
  .slick-slide {
    padding: 0 8px;
  }
  .slick-list {
    margin: 0 -8px;
  }
`

const CustomDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7f8c8d;

  .slick-active & {
    background-color: ${colors.PRIMARY};
  }
`

export default GameAttendanceCarousel
